import {
  LeftNavigationComponent,
  NavIcon,
  NavItemType,
  NavView,
  PrimaryNavItem,
  WorkflowNavItem
} from "@gravity/tools";
import { FreeTrialRole, useUserState, UUID } from "@gx1/gx-npm-lib";
import { toOutboundUrl, openOutbound } from "@gx1/gx-npm-buysmart";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import { navigateToUrl } from "single-spa";
import { MainNavAppV2Context } from "../../app-v2.context";
import styles from "./global-navigation.styles.module.scss";

const GlobalNavigationComponent = ({ view }: { view: NavView }) => {
  const { t } = useTranslation();
  const [navItems, setNavItems] = useState<PrimaryNavItem[]>([]);
  const [workflowItems, setWorkflowItems] = useState<WorkflowNavItem[]>([]);
  const { isMobilePrimaryNavOpen, setIsMobilePrimaryNavOpen } = useContext(MainNavAppV2Context);

  const { initiativeId = "", tab = "" } = useParams<{ initiativeId: UUID; tab: string }>();
  const { freeTrialRole, hasInitiatives, hasTasks, isEntitled } = useUserState();
  const { pathname } = useLocation();

  useEffect(() => {
    const items = [];
    const normalizedPathname = pathname.toLowerCase();
    if (isEntitled || hasInitiatives || !hasTasks) {
      items.push({
        href: "/s/home",
        icon: NavIcon.HOME,
        id: "home",
        isHighlighted: normalizedPathname.includes("/s/home"),
        label: t("Home"),
        onClick: () => {
          navigateToUrl("/s/home");
        },
        type: NavItemType.INTERNAL_LINK,
      });
      items.push({
        href: "/s/markets",
        icon:  NavIcon.MARKET_VENDOR,
        id: "market-vendor",
        isHighlighted: normalizedPathname.includes("/s/market"),
        label: t("Markets and Vendors"),
        onClick: () => {
          navigateToUrl("/s/markets");
        },
        type: NavItemType.INTERNAL_LINK,
      });
    }
    if (isEntitled || (freeTrialRole === FreeTrialRole.FREE_TRIAL_RECIPIENT)) {
      items.push({
        href: "/s/vendor-fit",
        icon: NavIcon.VENDOR_SHORTLISTING,
        id: "vendor-shortlisting",
        isHighlighted: normalizedPathname.includes("/s/vendor-fit"),
        label: t("Vendor Shortlisting"),
        onClick: () => {
          navigateToUrl("/s/vendor-fit");
        },
        type: NavItemType.INTERNAL_LINK,
      });
    }
    if (isEntitled || hasInitiatives || !hasTasks) {
      items.push({
        href: "/s/evaluations",
        icon: NavIcon.EVALUATIONS,
        id: "evaluations",
        isHighlighted: normalizedPathname.includes("/s/evaluations"),
        label: t("Evaluations"),
        onClick: () => {
          navigateToUrl("/s/evaluations");
        },
        type: NavItemType.INTERNAL_LINK,
      });
    }
    if (hasTasks) {
      items.push({
        href: "/s/tasks",
        icon: NavIcon.QUESTIONNAIRE_LIST,
        id: "tasks-dashboard",
        isHighlighted: normalizedPathname.includes("/s/tasks"),
        label: t("Questionnaire Tasks"),
        onClick: () => {
          navigateToUrl("/s/tasks");
        },
        type: NavItemType.INTERNAL_LINK,
      });
    }
    if (isEntitled) {
      items.push({
        href: toOutboundUrl(`${window.gcomWwwConnection}/x/proposal-review`),
        icon: NavIcon.PROPOSAL,
        id: "proposal-review",
        label: t("Proposal Review"),
        onClick: () => {
          openOutbound(`${window.gcomWwwConnection}/x/proposal-review`);
        },
        type: NavItemType.EXTERNAL_LINK,
      });
    }
    setNavItems(items);
  }, [
    freeTrialRole,
    hasInitiatives,
    hasTasks,
    initiativeId,
    isEntitled,
    pathname,
    setIsMobilePrimaryNavOpen,
    t,
  ]);

  useEffect(() => {
    setWorkflowItems([
      {
        className: "gx-dragon-tab-1",
        href: `/s/evaluation/${initiativeId}/overview`,
        id: "overview",
        label: t("Overview"),
        isHighlighted: tab === "overview",
        icon: NavIcon.OVERVIEW_DASHBOARD,
        onClick: () => navigateToUrl(`/s/evaluation/${initiativeId}/overview`),
      },
      {
        className: "gx-dragon-tab-2",
        href: `/s/evaluation/${initiativeId}/details`,
        id: "details",
        label: t("Details"),
        isHighlighted: tab === "details",
        icon: NavIcon.USE_CASE,
        onClick: () => navigateToUrl(`/s/evaluation/${initiativeId}/details`),
      },
      {
        className: "gx-dragon-tab-3",
        href: `/s/evaluation/${initiativeId}/checklist`,
        id: "checklist",
        label: t("Checklist"),
        isHighlighted: tab === "checklist",
        icon: NavIcon.CHECKLIST,
        onClick: () => navigateToUrl(`/s/evaluation/${initiativeId}/checklist`),
      },
      {
        className: "gx-dragon-tab-4",
        href:`/s/evaluation/${initiativeId}/requirements`,
        id: "requirements",
        label: t("Requirements"),
        isHighlighted: tab === "requirements",
        icon: NavIcon.REQUIREMENTS,
        onClick: () => navigateToUrl(`/s/evaluation/${initiativeId}/requirements`),
      },
      {
        className: "gx-dragon-tab-5",
        href:`/s/evaluation/${initiativeId}/products`,
        id: "vendors",
        label: t("Vendors"),
        isHighlighted: tab === "products",
        icon: NavIcon.VENDORS,
        onClick: () => navigateToUrl(`/s/evaluation/${initiativeId}/products`),
      },
      {
        className: "gx-dragon-tab-6 gx-rounds-tour",
        href: `/s/evaluation/${initiativeId}/rounds`,
        id: "rounds",
        label: "Evaluation Rounds",
        isHighlighted: tab === "rounds",
        icon: NavIcon.ANALYTICS,
        onClick: () => navigateToUrl(`/s/evaluation/${initiativeId}/rounds`),
        appendDivider: true,
      },
      {
        className: "gx-dragon-tab-7",
        href: `/s/evaluation/${initiativeId}/files/all`,
        id: "documents",
        label: t("Documents"),
        isHighlighted: pathname.toLowerCase().endsWith("files/all"),
        icon: NavIcon.DOCUMENTS,
        onClick: () => navigateToUrl(`/s/evaluation/${initiativeId}/files/all`),
      },
    ]);
  }, [initiativeId, pathname, t, tab]);

  return (
    <LeftNavigationComponent
      className={styles.gravityLeftNavigation}
      isMobilePrimaryNavOpen={isMobilePrimaryNavOpen}
      onToggleMobilePrimaryNav={(isOpen) => setIsMobilePrimaryNavOpen(isOpen)}
      primaryItems={navItems}
      view={view}
      workflowItems={workflowItems}
      zIndex={1100}
    />
  );
};

export default GlobalNavigationComponent;
